import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Card } from "@/components/ui";
import { useSoftphone } from "./SoftphoneProvider";
import { useSoftphoneStore } from "./store";

type Status = "idle" | "listening" | "denied";

/**
 * Prove the microphone works before a client is on the line.
 *
 * This is not a nicety. Every softphone product ships this screen eventually,
 * because without it the first symptom of a broken microphone is a seller
 * talking to a prospect who cannot hear them, and the support ticket says "the
 * CRM is broken" (.docs/04 §6).
 *
 * It doubles as the place the microphone permission is requested: once, from a
 * button the agent pressed. Asking on page load gets it denied silently by the
 * browser and there is no way back from that except browser settings.
 */
export function AudioCheck() {
  const { t } = useTranslation();
  const { prepare, chooseInput, chosenInput } = useSoftphone();
  const isDeviceReady = useSoftphoneStore((state) => state.isDeviceReady);

  const [status, setStatus] = useState<Status>("idle");
  const [level, setLevel] = useState(0);
  const [inputs, setInputs] = useState<MediaDeviceInfo[]>([]);
  // Opens on the microphone calls will use, so the meter tests that one and
  // not whichever the operating system happens to call its default.
  const [selected, setSelected] = useState(() => chosenInput() ?? "");

  const streamRef = useRef<MediaStream | null>(null);
  const contextRef = useRef<AudioContext | null>(null);
  const frameRef = useRef<number | null>(null);

  const teardown = useCallback(() => {
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    for (const track of streamRef.current?.getTracks() ?? []) track.stop();
    streamRef.current = null;
    void contextRef.current?.close();
    contextRef.current = null;
    setLevel(0);
  }, []);

  // Releasing the microphone on unmount is not housekeeping: a live track
  // leaves the browser's recording indicator lit on the tab all day, which
  // reads to the agent as the CRM listening to them.
  useEffect(() => teardown, [teardown]);

  const listen = useCallback(
    async (deviceId?: string) => {
      teardown();
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: deviceId ? { deviceId: { exact: deviceId } } : true,
        });
        streamRef.current = stream;

        // Labels are empty until permission is granted, so the picker can only
        // be populated after the first successful `getUserMedia` — never before.
        setInputs(
          (await navigator.mediaDevices.enumerateDevices()).filter(
            (device) => device.kind === "audioinput",
          ),
        );

        const context = new AudioContext();
        contextRef.current = context;
        const analyser = context.createAnalyser();
        analyser.fftSize = 512;
        context.createMediaStreamSource(stream).connect(analyser);

        const samples = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
          analyser.getByteTimeDomainData(samples);
          // Peak deviation from silence (128), as a 0–1 fraction. RMS would be
          // steadier but reads too low to look alive when someone says "hola".
          let peak = 0;
          for (const sample of samples) {
            peak = Math.max(peak, Math.abs(sample - 128) / 128);
          }
          setLevel(peak);
          frameRef.current = requestAnimationFrame(tick);
        };
        tick();
        setStatus("listening");
        await prepare();
      } catch {
        // A remembered headset that is not plugged in today is not a denied
        // permission. Test the default instead of telling the agent to go and
        // dig through browser settings.
        if (deviceId) {
          setSelected("");
          return listen();
        }
        setStatus("denied");
      }
    },
    [prepare, teardown],
  );

  /** A second of a quiet tone, so the agent can confirm which output is live. */
  const testOutput = useCallback(() => {
    const context = new AudioContext();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.frequency.value = 440;
    gain.gain.value = 0.08;
    oscillator.connect(gain).connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + 1);
    oscillator.onended = () => void context.close();
  }, []);

  return (
    <Card title={t("audio.title")}>
      <div className="space-y-4 p-4 text-sm">
        <p className="text-mute-600">{t("audio.why")}</p>

        {status === "denied" ? (
          <p className="text-brand-700">{t("audio.denied")}</p>
        ) : null}

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => void listen(selected || undefined)}
            className="btn btn-secondary px-3 py-1.5"
          >
            {t(status === "listening" ? "audio.restart" : "audio.start")}
          </button>
          <button
            type="button"
            onClick={testOutput}
            className="btn btn-secondary px-3 py-1.5"
          >
            {t("audio.testOutput")}
          </button>
        </div>

        {status === "listening" ? (
          <>
            <label className="block">
              <span className="text-mute-600">{t("audio.input")}</span>
              <select
                value={selected}
                onChange={(event) => {
                  const deviceId = event.target.value;
                  setSelected(deviceId);
                  chooseInput(deviceId || null);
                  void listen(deviceId || undefined);
                }}
                className="input mt-1"
              >
                <option value="">{t("audio.defaultInput")}</option>
                {inputs.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || device.deviceId}
                  </option>
                ))}
              </select>
            </label>

            <div>
              <span className="text-mute-600">{t("audio.level")}</span>
              <div
                role="meter"
                aria-valuenow={Math.round(level * 100)}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={t("audio.level")}
                className="mt-1 h-3 w-full overflow-hidden bg-mute-300"
              >
                <div
                  className="h-full bg-brand transition-[width] duration-75"
                  style={{ width: `${Math.min(100, level * 140)}%` }}
                />
              </div>
              <p className="mt-1 text-mute-600">{t("audio.speak")}</p>
            </div>
          </>
        ) : null}

        <p className={isDeviceReady ? "text-brand-700" : "text-mute-600"}>
          {t(isDeviceReady ? "audio.ready" : "audio.notReady")}
        </p>
      </div>
    </Card>
  );
}
